export class GameSimulation {
  constructor(gameInterface, users, width, height) {
    this.gameInterface = gameInterface;
    this.gameTimer = 300000; // 5 minutes in ms
    this.isGameOver = false;
    
    this.gameState = {
      entities: {},
      screenSize: {
        width,
        height
      },
      worldSize: {
        width: 1200,
        height: 1200
      },
      timeRemaining: this.gameTimer,
      isGameOver: this.isGameOver,
      coldIntensity: 1,
      coldIntensityTimer: 30000, // 30 seconds
      notification: null,
      resourceCounts: {
        bush: 0,
        tree: 0
      },
      resourceGainEffects: [] // New array to track resource gain effects
    };

    Object.keys(users).forEach(userId => {
      this.gameState.entities[userId] = {
        id: userId,
        type: 'player',
        name: users[userId].name,
        x: 600 + (Math.random() - 0.5) * 200,
        y: 600 + (Math.random() - 0.5) * 200,
        dx: 0,
        dy: 0,
        state: 'idle',
        direction: 'right',
        hunger: 80,
        cold: 80,
        fruits: 0,
        logs: 0,
        speed: 0.15,
        isDead: false,
        nearbyResources: [], // Track nearby resources
        gatheringTimer: 0,
        gatheringTarget: null
      };
    });

    // Initial resource spawn
    for (let i = 0; i < 10; i++) {
      this.spawnResource('bush');
      this.spawnResource('tree');
    }

    this.gameInterface.on('joystick_move', (data) => {
      const entity = this.gameState.entities[data.userId];
      if (entity && !entity.isDead) {
        entity.dx = data.dx;
        entity.dy = data.dy;
        if (data.dx !== 0 || data.dy !== 0) {
          entity.state = 'walking';
          entity.gatheringTimer = 0;
          entity.gatheringTarget = null;
        } else {
          entity.state = entity.gatheringTimer > 0 ? 'gathering' : 'idle';
        }
        if (data.dx !== 0) {
          entity.direction = data.dx > 0 ? 'right' : 'left';
        }
      }
    });

    this.gameInterface.on('eat', (data) => {
      const player = this.gameState.entities[data.userId];
      if (!player || player.isDead || player.fruits <= 0) return;
      
      player.fruits--;
      player.hunger = Math.min(100, player.hunger + 30);
    });

    this.gameInterface.on('build_fire', (data) => {
      const player = this.gameState.entities[data.userId];
      if (!player || player.isDead || player.logs < 5) return;

      player.logs -= 5;
      const fireId = `fire${Date.now()}`;
      this.gameState.entities[fireId] = {
        id: fireId,
        type: 'fire',
        x: player.x,
        y: player.y + 20,
        timeRemaining: 30000
      };
    });
  }

  spawnResource(type) {
    const id = `${type}${Date.now()}_${Math.random()}`;
    this.gameState.entities[id] = {
      id,
      type,
      x: Math.random() * this.gameState.worldSize.width,
      y: Math.random() * this.gameState.worldSize.height,
      [type === 'bush' ? 'fruits' : 'logs']: 3
    };
    this.gameState.resourceCounts[type]++;
  }

  checkCollision(entity1, entity2, radius = 40) {
    const y1 = entity1.type === 'fire' ? entity1.y - 20 : entity1.y;
    const y2 = entity2.type === 'fire' ? entity2.y - 20 : entity2.y;
    
    const distance = Math.sqrt(
      Math.pow(entity1.x - entity2.x, 2) + 
      Math.pow(y1 - y2, 2)
    );
    return distance < radius;
  }

  getResults() {
    const survivors = Object.values(this.gameState.entities)
      .filter(e => e.type === 'player' && !e.isDead)
      .map(p => p.name);
    
    return {
      winners: survivors,
      losers: Object.values(this.gameState.entities)
        .filter(e => e.type === 'player' && e.isDead)
        .map(p => p.name)
    };
  }

  update(deltaTime) {
    if (this.isGameOver) return;

    this.gameTimer -= deltaTime;
    this.gameState.timeRemaining = Math.max(0, this.gameTimer);
    
    // Update cold intensity
    this.gameState.coldIntensityTimer -= deltaTime;
    if (this.gameState.coldIntensityTimer <= 0) {
      this.gameState.coldIntensity += 0.5;
      this.gameState.coldIntensityTimer = 30000;
      this.gameState.notification = {
        message: `The cold intensifies! Cold damage is now ${this.gameState.coldIntensity}x`,
        duration: 3000
      };
    }
    
    // Update resource gain effects
    this.gameState.resourceGainEffects = this.gameState.resourceGainEffects.filter(effect => {
      effect.timer -= deltaTime;
      effect.y -= deltaTime * 0.05; // Float up
      return effect.timer > 0;
    });
    
    // Reset resource counts
    this.gameState.resourceCounts.bush = 0;
    this.gameState.resourceCounts.tree = 0;
    
    // Count and clean up resources
    Object.entries(this.gameState.entities).forEach(([id, entity]) => {
      if (entity.type === 'bush') {
        if (entity.fruits <= 0) {
          delete this.gameState.entities[id];
        } else {
          this.gameState.resourceCounts.bush++;
        }
      }
      if (entity.type === 'tree') {
        if (entity.logs <= 0) {
          delete this.gameState.entities[id];
        } else {
          this.gameState.resourceCounts.tree++;
        }
      }
    });
    
    // Spawn new resources if needed
    while (this.gameState.resourceCounts.bush < 10) {
      this.spawnResource('bush');
    }
    while (this.gameState.resourceCounts.tree < 10) {
      this.spawnResource('tree');
    }
    
    Object.values(this.gameState.entities).forEach(entity => {
      if (entity.type === 'player' && !entity.isDead) {
        entity.x += entity.dx * deltaTime * entity.speed;
        entity.y += entity.dy * deltaTime * entity.speed;

        entity.x = Math.max(0, Math.min(entity.x, this.gameState.worldSize.width));
        entity.y = Math.max(0, Math.min(entity.y, this.gameState.worldSize.height));

        entity.hunger -= 0.001 * deltaTime;
        entity.cold -= 0.001 * deltaTime * this.gameState.coldIntensity;

        entity.nearbyResources = Object.values(this.gameState.entities)
          .filter(other => 
            (other.type === 'bush' || other.type === 'tree') &&
            this.checkCollision(entity, other, 60)
          )
          .map(r => ({id: r.id, type: r.type}));

        let nearFire = false;
        Object.values(this.gameState.entities).forEach(other => {
          if (other.type === 'fire' && this.checkCollision(entity, other, 100)) {
            nearFire = true;
          }
        });

        if (nearFire) {
          entity.cold = Math.min(100, entity.cold + 0.02 * deltaTime);
        }

        if (entity.state === 'idle' && entity.gatheringTimer <= 0) {
          const nearbyResource = entity.nearbyResources[0];
          if (nearbyResource) {
            const resource = this.gameState.entities[nearbyResource.id];
            if (resource) {
              if ((resource.type === 'bush' && resource.fruits > 0) ||
                  (resource.type === 'tree' && resource.logs > 0)) {
                entity.state = 'gathering';
                entity.gatheringTimer = 1000;
                entity.gatheringTarget = nearbyResource;
              }
            }
          }
        }

        if (entity.gatheringTimer > 0) {
          entity.gatheringTimer -= deltaTime;
          if (entity.gatheringTimer <= 0) {
            if (entity.gatheringTarget) {
              const target = this.gameState.entities[entity.gatheringTarget.id];
              if (target) {
                if (target.type === 'bush' && target.fruits > 0) {
                  target.fruits--;
                  entity.fruits++;
                  // Add resource gain effect
                  this.gameState.resourceGainEffects.push({
                    type: 'fruit',
                    x: entity.x,
                    y: entity.y - 20,
                    timer: 1000
                  });
                } else if (target.type === 'tree' && target.logs > 0) {
                  target.logs--;
                  entity.logs++;
                  // Add resource gain effect
                  this.gameState.resourceGainEffects.push({
                    type: 'wood',
                    x: entity.x,
                    y: entity.y - 20,
                    timer: 1000
                  });
                }
              }
            }
            entity.gatheringTarget = null;
            entity.state = 'idle';
          }
        }

        if (entity.hunger <= 0 || entity.cold <= 0) {
          entity.isDead = true;
        }
      }

      if (entity.type === 'fire') {
        entity.timeRemaining -= deltaTime;
        if (entity.timeRemaining <= 0) {
          delete this.gameState.entities[entity.id];
        }
      }
    });

    if (this.gameState.notification) {
      this.gameState.notification.duration -= deltaTime;
      if (this.gameState.notification.duration <= 0) {
        this.gameState.notification = null;
      }
    }

    const alivePlayers = Object.values(this.gameState.entities)
      .filter(e => e.type === 'player' && !e.isDead);

    if (alivePlayers.length <= 1 || this.gameTimer <= 0) {
      this.isGameOver = true;
      this.gameState.isGameOver = true;
      this.gameState.results = this.getResults();
    }
  }
}
